import { FiInfo } from 'react-icons/fi';

/**
 * Material summary component for individual file analysis.
 * Shows material info, weight, and Section 232/301 indicators.
 */
export default function IndividualMaterialSummary({
    material,
    materialParent,
    weightKg,
    weightStatus,
    sec232,
    sec301,
}) {
    const isSec232Material = materialParent === 'Steel' || materialParent === 'Aluminum';
    const hasSec301Risk = sec301?.applies === true;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5">
                <h3 className="text-lg font-semibold text-[#1e5045] mb-4">
                    Material Summary
                </h3>

                {/* Material Info Row */}
                <div className="space-y-4">
                    {/* Material Name & Category */}
                    <div className={`flex items-center justify-between p-3 rounded-lg ${
                        isSec232Material
                            ? 'bg-[#f26522]/10 border border-[#f26522]/30'
                            : 'bg-[#f6f4ed]'
                    }`}>
                        <div className="flex items-center gap-3">
                            {isSec232Material && (
                                <span className="text-xs font-medium text-[#f26522] bg-[#f26522]/20 px-2 py-0.5 rounded">
                                    Sec 232
                                </span>
                            )}
                            <div>
                                <div className="font-semibold text-gray-900">
                                    {material || 'Unknown Material'}
                                </div>
                                <div className="text-xs text-gray-500">
                                    Category: {materialParent || 'Other'}
                                </div>
                            </div>
                        </div>
                        {isSec232Material && (
                            <div className="text-right">
                                <span className="text-xs text-[#f26522] font-medium">
                                    25% Tariff
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Weight Info */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                            <span className="text-gray-600 font-medium">Weight</span>
                            {weightStatus && (
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                    weightStatus === 'Explicitly Stated'
                                        ? 'bg-green-100 text-green-700'
                                        : weightStatus === 'Calculated'
                                            ? 'bg-purple-100 text-purple-700'
                                            : weightStatus === 'Estimated'
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'bg-amber-100 text-amber-700'
                                }`}>
                                   {weightStatus === 'Explicitly Stated' ? 'Stated' 
                                     : weightStatus === 'Calculated' ? 'Calculated'
                                     : weightStatus === 'Estimated' ? 'Estimated'
                                     : weightStatus}
                                </span>
                            )}
                        </div>
                        <span className="text-lg font-bold text-[#1e5045]">
                            {weightKg ? `${weightKg.toFixed(2)} kg` : 'N/A'}
                        </span>
                    </div>

                    {/* Section 301 Info (informational) */}
                    {hasSec301Risk && (
                        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <FiInfo className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="text-sm font-medium text-amber-800">
                                    HTS Code Subject to Section 301
                                </div>
                                <div className="text-xs text-amber-600 mt-0.5">
                                    {sec301.list && `List ${sec301.list}`}
                                    {sec301.ratePercent && ` (${sec301.ratePercent}% tariff)`}
                                    {' '}- Applies if Country of Origin is China
                                </div>
                            </div>
                        </div>
                    )}

                    {/* No Sec 232 or 301 risks */}
                    {!isSec232Material && !hasSec301Risk && (
                        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm text-green-700">
                                No Section 232 or 301 tariff exposure detected
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
